import {
    ConcatFieldType,
    ConditionIndicatorType,
    ConditionRuleType,
    SourceFieldType,
    SourceFileType,
    StatementType,
} from "../types";
import { CompareType } from "./constants";
import { coreFields } from "services/MetadataService";

export const isMeta1_5 = (value: string) =>
    coreFields.some(
        (e) => e.label === value && [3, 4, 8, 9, 10].includes(e.value),
    );

export const getExtension = (fileType: string, sourceType: SourceFileType) => {
    const extension =
        fileType === "XML" || sourceType === "single" ? "META" : "META_EXT";
    return `${fileType}_${extension}`;
};

export const hasInvalidIndicators = (indicators?: ConditionIndicatorType[]) => {
    let hasError = false;
    if (!indicators) {
        return hasError;
    }
    for (let i = 0; i < indicators.length; i++) {
        const indicator = indicators[i];
        // If compare type is exists, we don't need a value
        if (
            !indicator.sourceName ||
            (indicator.compareType !== CompareType.Exists && !indicator.value)
        ) {
            indicator.hasError = true;
            hasError = true;
        } else {
            indicator.hasError = false;
        }
    }
    return hasError;
};

export const statementIsInvalid = (statement: StatementType) => {
    let hasError = false;
    if (hasInvalidIndicators(statement.conditionIndicators)) {
        hasError = true;
    }
    // If isLiteral is true, result should be a string
    if (statement.isLiteral && !statement.result) {
        hasError = true;
    }
    // If isLiteral is false (not undefined), sourceName should be a string
    if (statement.isLiteral === false && !statement.sourceName) {
        hasError = true;
    }

    // Each statement with conditions should have a result or sourceName or nested statement
    if (
        statement.conditionIndicators?.length &&
        ((!statement.else?.result &&
            !statement.else?.sourceName &&
            !statement.else?.then) ||
            (!statement.then?.result &&
                !statement.then?.sourceName &&
                !statement.then?.then))
    ) {
        hasError = true;
    }
    return hasError;
};

export const updateStatementErrors = (statement?: StatementType) => {
    if (!statement) {
        return undefined;
    }

    return {
        ...statement,
        hasError: statementIsInvalid(statement),
        then: updateStatementErrors(statement.then),
        else: updateStatementErrors(statement.else),
    };
};

// returns true if the statement or any of its children have an error
export const checkIfStatementHasError = (
    statement?: StatementType | ConditionRuleType,
) => {
    if (!statement) {
        return false;
    }

    if (statement.hasError) {
        return true;
    }

    if (checkIfStatementHasError(statement.then)) {
        return true;
    }

    if (checkIfStatementHasError(statement.else)) {
        return true;
    }

    return false;
};

const updateStatements = (
    statement: ConditionRuleType | StatementType | undefined,
    fieldBeforeChange: SourceFieldType | undefined,
    updatedField: SourceFieldType | undefined,
) => {
    if (!statement) {
        return;
    }

    statement.conditionIndicators = statement.conditionIndicators?.map((ci) => {
        if (ci.sourceName === fieldBeforeChange?.alias) {
            return {
                ...ci,
                sourceName: updatedField?.alias || "",
            };
        }
        return ci;
    });

    return {
        ...statement,
        then: updateStatements(statement.then, fieldBeforeChange, updatedField),
        else: updateStatements(statement.else, fieldBeforeChange, updatedField),
    };
};

// Update the condition rules and concat fields when a source field is updated
// This allows the aliases/paths used in rules to stay in sync with the source fields
export const runUpdatesWhenSourceChanges = (
    previousFields: SourceFieldType[],
    updatedFields: SourceFieldType[],
    conditionRules: ConditionRuleType[],
    concatFields: ConcatFieldType[],
) => {
    // get field that will be updated
    const fieldBeforeChange = previousFields.find((field) =>
        updatedFields.some(
            (f) =>
                f.id === field.id &&
                (f.alias !== field.alias || f.path !== field.path),
        ),
    );

    // get the updated field
    const updatedField = updatedFields.find(
        (field) => field.id === fieldBeforeChange?.id,
    );

    // find all concat rules that contain the field that changed
    const updatedConcatFields = concatFields.map((concatField) => ({
        ...concatField,
        fields: concatField.fields.map((field) =>
            field.type === "field" && field.value === fieldBeforeChange?.alias
                ? { ...field, value: updatedField?.alias || "" }
                : field,
        ),
    }));

    // find all condition rules that contain the field that changed
    const updatedConditionRules = conditionRules.map((rule) =>
        updateStatements(rule, fieldBeforeChange, updatedField),
    );

    return {
        updatedConcatFields,
        updatedConditionRules,
    };
};
